import React, { useState } from 'react';
import { Drawer, Form, Select, Button, Input, message } from 'antd';
import { raiseDeliveryEscalation } from '../deliveryApi';
import { usePermissions } from "../../../../custom_hooks/permission/permission_hook";
import ExistingTicketsBanner from "../../../oms/logistics/components/existing_tickets_banner";

const { Option, OptGroup } = Select;

const ESCALATION_ISSUES: Record<string, string[]> = {
  "Procurement": [
    "Material Unavailable",
    "Vendor Payment Issue",
    "Data/PO Error",
  ],
  "Delivery": [
    "Vehicle Delayed",
    "Site Closing Time",
    "Vehicle Not Found",
    "Missed by Delivery Team",
    "Unloaders Not Available",
  ],
  "CS": [
    "Reschedule Request",
    "Incorrect Address",
    "Cancellation Request",
  ],
};

interface EscalationDrawerProps {
  data: any;
  onClose: () => void;
  onSuccess: () => void;
}

export const EscalationDrawer: React.FC<EscalationDrawerProps> = ({ data, onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission("delivery.update");
  const [submitting, setSubmitting] = useState(false);
  const issueType = Form.useWatch("issue_type", form);

  const handleSubmit = async (values: any) => {
    if (!canEdit) {
      message.error("You don't have permission to raise an escalation");
      return;
    }
    setSubmitting(true);
    const result = await raiseDeliveryEscalation({
      po_delivery_id: data?.id,
      issue_type: values.issue_type,
      remarks: values.remarks?.trim() || undefined,
    });
    setSubmitting(false);
    if (result) {
      form.resetFields();
      onClose();
      onSuccess();
    }
  };

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Drawer
      title={`Raise Escalation - ${data?.po?.po_number || ''}`}
      open={!!data}
      onClose={handleClose}
      width={420}
    >
      {data && (
        <ExistingTicketsBanner poNumber={data?.po?.po_number} issueType={issueType} />
      )}
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          name="issue_type"
          label="Issue Type"
          rules={[{ required: true, message: "Please select an issue type" }]}
        >
          <Select placeholder="Select issue type">
            {Object.entries(ESCALATION_ISSUES).map(([group, issues]) => (
              <OptGroup key={group} label={group}>
                {issues.map((issue) => (
                  <Option key={issue} value={issue}>
                    {issue}
                  </Option>
                ))}
              </OptGroup>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="remarks" label="Anything else the team should know?">
          <Input.TextArea rows={3} maxLength={500} showCount placeholder="Optional" />
        </Form.Item>
        <Button type="primary" danger htmlType="submit" block loading={submitting} disabled={!canEdit}>
          Raise Escalation
        </Button>
      </Form>
    </Drawer>
  );
};
